import React, { FC, useEffect, useState } from "react";
import classNames from "classnames";
import { useSelect } from "downshift";

import { DoctorDto } from "@apiModels/DoctorDto.csharp";
import { FieldStatus } from "../../../Helpers/FieldStatus";
import { LoadingAnimation } from "../../../../LoadingAnimation/LoadingAnimation";
import { SvgIcon } from "../../../../SvgIcon/SvgIcon";

type DoctorDropdpwnProps = {
  doctors: DoctorDto[];
  label: string;
  labelSelected: string;
  placeholder: string;
  onSelect: (doctor: DoctorDto) => void;
  doctor: DoctorDto;
  defaultValue: DoctorDto;
  status: FieldStatus;
};

export const DoctorDropdown: FC<DoctorDropdpwnProps> = ({
  doctors,
  label,
  labelSelected,
  placeholder,
  onSelect,
  doctor,
  defaultValue,
  status,
}) => {
  const [doctorsToShow, setDoctorsToShow] = useState<DoctorDto[]>([]);
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    selectItem,
  } = useSelect<DoctorDto | null>({ items: doctorsToShow });

  useEffect(() => {
    const activeDoctors = doctors.filter(
      (d) => !!d.id && d.id != "-1" && d.isNotADoctor == false
    );
    const allDoctors = doctors.filter((d) => !!d?.id && d.id != "-1");
    if (
      allDoctors.length == 1 &&
      activeDoctors.length == 1 &&
      !!activeDoctors[0]
    ) {
      selectItem(activeDoctors[0]);
      onSelect(activeDoctors[0]);
    }

    setDoctorsToShow(doctors.filter((d) => d.isNotADoctor == false));
  }, [doctors]);

  useEffect(() => {
    if (!doctor || !doctor.id) {
      selectItem(null);
    }
  }, [doctor]);

  useEffect(() => {
    if (selectedItem != null && selectedItem.id) {
      onSelect(selectedItem);
    }
  }, [selectedItem]);

  const madeSelection =
    selectedItem != null || (defaultValue && defaultValue.id);

  const selectedValueLabel = selectedItem?.name ?? defaultValue?.name ?? "";

  const renderDropdownLabel = () => {
    if (status === FieldStatus.disabled && madeSelection) {
      return <LoadingAnimation text={selectedValueLabel} />;
    }
    if (!madeSelection && status === FieldStatus.disabled) {
      return <LoadingAnimation text={placeholder} />;
    }
    if (!madeSelection && status === FieldStatus.active) {
      return placeholder;
    }
    return selectedValueLabel;
  };

  return (
    <div
      className={classNames(
        "DoctorDropdown",
        {
          "DoctorDropdown--opened": isOpen,
          "DoctorDropdown--disabled": status == FieldStatus.disabled,
          "DoctorDropdown--error": status == FieldStatus.error,
        }
      )}
    >
      <span {...getLabelProps()} className="DoctorDropdown__label">
        {label}
      </span>
      <button
        className="DoctorDropdown__field"
        type="button"
        {...getToggleButtonProps()}
        disabled={status == FieldStatus.disabled}
      >
        {renderDropdownLabel()}
        {status != FieldStatus.disabled && <SvgIcon iconName="chevron-down" className="DoctorDropdown__icon" />}
      </button>
      <ul className="DoctorDropdown__optionsList" {...getMenuProps()}>
        {isOpen && (
          <>
            {doctorsToShow.map((item, index) => (
              <li
                key={item!.id}
                className={classNames("DoctorDropdown__option", {
                  "DoctorDropdown__option--highlighted": highlightedIndex === index,
                })}
                {...getItemProps({ item, index })}
              >
                <span className="DoctorDropdown__optionName">{item.name}</span>
                {item!.jobTitle && (
                  <span className="DoctorDropdown__optionDescription">{item!.jobTitle}</span>
                )}
              </li>
            ))}
          </>
        )}
      </ul>
    </div>
  );
};
